import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { filterOutBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/filterOutBackgroundCheckRequirements";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { type AxiosError } from "axios";

import { useDocumentExclusionFilter } from "../../hooks/useDocumentExclusionFilter";
import { mergeWorkerRequirementWithIncludedResources } from "../mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../types";
import { useGetWorkerRequirements } from "../useGetWorkerRequirements";

interface UseGetMandatoryWorkerRequirementsProps {
  qualifications: string[];
}

export function useGetMandatoryWorkerRequirements(
  props: UseGetMandatoryWorkerRequirementsProps,
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const { qualifications } = props;
  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();
  const filterDocuments = useDocumentExclusionFilter();

  return useGetWorkerRequirements(
    {
      filter: {
        qualifications,
        requiredForBooking: true,
        levels: [
          WorkerRequirementMappingLevel.GLOBAL,
          WorkerRequirementMappingLevel.STATE,
          WorkerRequirementMappingLevel.COUNTY,
          WorkerRequirementMappingLevel.MSA,
        ],
        visibleToWorker: true,
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings],
    },
    {
      select: (data) => {
        const mergedData = mergeWorkerRequirementWithIncludedResources(data);
        const filteredData = filterDocuments(mergedData);

        if (isRunBackgroundCheckOnBookingEnabled) {
          return filterOutBackgroundCheckRequirements(filteredData, {
            status: [WorkerRequirementStatus.MISSING],
          });
        }

        return filteredData;
      },
      ...options,
    }
  );
}
